import { CircularProgress } from '@mui/material';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import {
  useExplicitConsent,
  useGrantExplicitConsent,
} from '../../../../shared/hooks/explicitConsent';
import {
  useRequestOnlinePaymentConsent,
  useVerifyOnlinePaymentConsent,
} from '../../../../shared/hooks/onlinePaymentConsentHook';
import { usePay } from '../../../../shared/hooks/paymentHook';
import { BlueRoundedButton } from '../../common/components/BlueRoundedButton';
import {
  CheckboxText,
  CheckboxWithText,
} from '../../common/components/CheckboxWithText';
import { useModalityLabel } from '../../hooks/modalityHook';
import { LandingLayoutHeader } from '../Layout/LayoutHeader';
import { LayoutBackground } from '../Layout/components';

import { ConsentSuccessContent } from './ConsentSuccessContent';
import {
  ConsentButtonContainer,
  ConsentContent,
  ConsentFormContainer,
  DateText,
  ErrorMessage,
  LocalIssuedImageListTable,
  ProgressbarContainer,
  Title,
} from './components';

export const ExplicitConsent = () => {
  const { uuid } = useParams();
  const {
    data: explicitConsent,
    isFetching,
    error,
  } = useExplicitConsent({
    uuid: uuid as string,
  });
  const {
    mutate: grantExplicitConsent,
    isPending: isPendingGrantExplicitConsent,
    isSuccess: isSuccessGrantExplicitConsent,
  } = useGrantExplicitConsent({
    uuid: uuid as string,
  });

  const { isPendingRequestPayment, isSuccessVerifyPayment, requestPayment } =
    useOnlinePaymentConsent();
  const [check, setCheck] = useState(false);

  const studies = useMemo(() => {
    return (
      explicitConsent?.content.sort((a, b) => {
        return (
          moment(b.studyDate, 'YYYY-MM-DD').valueOf() -
          moment(a.studyDate, 'YYYY-MM-DD').valueOf()
        );
      }) ?? []
    );
  }, [explicitConsent]);

  const { t } = useTranslation('translation', {
    keyPrefix: 'features.explicitConsent',
  });
  const { t: globalT } = useTranslation('translation');
  const modalityLabel = useModalityLabel();

  return (
    <LayoutBackground>
      <LandingLayoutHeader />
      {isFetching ? (
        <ProgressbarContainer>
          <CircularProgress />
        </ProgressbarContainer>
      ) : explicitConsent ? (
        isSuccessVerifyPayment || isSuccessGrantExplicitConsent ? (
          <ConsentSuccessContent
            isOnlinePayment={explicitConsent.isOnlinePayment}
          />
        ) : (
          <ConsentFormContainer>
            <Title>{t('title')}</Title>
            <LocalIssuedImageListTable>
              <thead>
                <tr>
                  <th>{t('table.header.number')}</th>
                  <th>{t('table.header.studyDate')}</th>
                  <th>{t('table.header.images')}</th>
                </tr>
              </thead>
              <tbody>
                {studies.map((study, index) => (
                  <tr key={index}>
                    <td align="center">{index + 1}</td>
                    <td align="center">
                      {study.studyDate || globalT('history.unknown')}
                    </td>
                    <td align="center">
                      {study.modality
                        ? study.modality
                            .split(',')
                            .map(modality => modalityLabel(modality))
                            .join(', ')
                        : globalT('history.unknown')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </LocalIssuedImageListTable>
            <DateText>{new Date().toLocaleDateString()}</DateText>
            <ConsentContent>
              {explicitConsent.isOnlinePayment ? (
                <CheckboxWithText
                  text={<CheckboxText>{t('content')}</CheckboxText>}
                  $margin="18px 0 0 0"
                  value={check}
                  onChange={setCheck}
                />
              ) : (
                t('content')
              )}
            </ConsentContent>
            <ConsentButtonContainer>
              <BlueRoundedButton
                disabled={explicitConsent.isOnlinePayment && !check}
                onClick={() => {
                  explicitConsent.isOnlinePayment
                    ? requestPayment({ uuid: uuid as string })
                    : grantExplicitConsent(uuid);
                }}>
                {explicitConsent.isOnlinePayment ? (
                  isPendingRequestPayment || isPendingGrantExplicitConsent ? (
                    <CircularProgress size={24} style={{ color: 'white' }} />
                  ) : (
                    `${t('button.payment')}`
                  )
                ) : (
                  `${t('button.consent')}`
                )}
              </BlueRoundedButton>
            </ConsentButtonContainer>
          </ConsentFormContainer>
        )
      ) : error?.response?.status === 404 ? (
        <ErrorMessage>{t('errorMessage.notFoundError')}</ErrorMessage>
      ) : (
        <ErrorMessage>{t('errorMessage.unknownError')}</ErrorMessage>
      )}
    </LayoutBackground>
  );
};

const useOnlinePaymentConsent = () => {
  const { mutate: verifyPayment, isSuccess: isSuccessVerifyPayment } =
    useVerifyOnlinePaymentConsent();
  const { pay } = usePay({
    paymentName: '의료영상 발급',
    onSuccessPay: verifyPayment,
    mobileRedirectUrlPath: '/mobile-payment-callback/local-issuance-online',
  });
  const { mutate: requestPayment, isPending: isPendingRequestPayment } =
    useRequestOnlinePaymentConsent({
      onSuccess(data) {
        const merchantId = data.id;
        if (merchantId) {
          pay(merchantId, data.price.amount, data.price.taxFree);
        }
      },
    });

  return {
    isSuccessVerifyPayment,
    isPendingRequestPayment,
    requestPayment,
  };
};
